import {
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Table,
  Tabs,
  Typography,
} from "@mui/joy";
import {
  AbilityCategory,
  ApiQuestCostClimbType,
  ApiQuestCostEstimate,
  ApiQuestCostScenario,
} from "griptonite-api-client/types";
import { ReactNode } from "react";

import { getAbilityCategoryLabel } from "@/utils/abilityCategory";

import { BONUS_TRIGGERS } from "./bonusForms";

const SCENARIOS = [
  { key: "low", label: "Low" },
  { key: "central", label: "Central" },
  { key: "high", label: "High" },
] as const;

const CLIMB_TYPE_LABELS: Record<string, string> = {
  BOULDER: "Bouldering",
  SPORT: "Sport",
};

const count = (value: number) => Math.round(value).toLocaleString();
const decimal = (value: number, digits = 1) =>
  value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
const percent = (share: number) =>
  share.toLocaleString(undefined, { style: "percent", maximumFractionDigits: 1 });

/** The model's workings behind a quest's cost estimate, scenario by scenario. */
export default function QuestCostDetailsModal({
  estimate,
  open,
  onClose,
}: {
  estimate: ApiQuestCostEstimate;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 960, maxWidth: "calc(100vw - 32px)" }}>
        <ModalClose />
        <DialogTitle>How the estimate is worked out</DialogTitle>
        <DialogContent>
          <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
            The model takes the gym&apos;s last three months of climbing, about{" "}
            {count(estimate.activeLoggers)} people logging climbs each month, and works out what a
            typical participant of each ability would earn. Each scenario uses low, central or high
            values for how many people join and how much they log.
          </Typography>
          <Tabs defaultValue="central" sx={{ backgroundColor: "transparent" }}>
            <TabList>
              {SCENARIOS.map(({ key, label }) => (
                <Tab key={key} value={key}>
                  {label}
                </Tab>
              ))}
            </TabList>
            {SCENARIOS.map(({ key }) => {
              const scenario = estimate[key];
              return (
                <TabPanel key={key} value={key} sx={{ paddingX: 0 }}>
                  {scenario ? <ScenarioDetails scenario={scenario} /> : null}
                </TabPanel>
              );
            })}
          </Tabs>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}

const ScenarioDetails = ({ scenario }: { scenario: ApiQuestCostScenario }) => (
  <Stack gap={3}>
    <Stack direction="row" gap={3} flexWrap="wrap">
      <Stat label="Credits" value={count(scenario.credits)} />
      <Stat label="Participants" value={count(scenario.participants)} />
      <Stat label="Credits per participant" value={count(scenario.creditsPerParticipant)} />
      <Stat
        label="Participants per active logger"
        value={decimal(scenario.participantsPerActiveLogger, 2)}
      />
    </Stack>
    {scenario.climbTypes.map((climbType) => (
      <ClimbTypeDetails key={climbType.climbType} climbType={climbType} />
    ))}
  </Stack>
);

const ClimbTypeDetails = ({ climbType }: { climbType: ApiQuestCostClimbType }) => (
  <Stack gap={2}>
    <Typography level="h4">
      {CLIMB_TYPE_LABELS[climbType.climbType] ?? climbType.climbType}
    </Typography>
    <Stack direction="row" gap={3} flexWrap="wrap">
      <Stat label="Active loggers" value={count(climbType.activeLoggers)} />
      <Stat label="Participants" value={count(climbType.participants)} />
      <Stat label="Milestone credits" value={count(climbType.thresholdCredits)} />
      <Stat label="Side quest credits" value={count(climbType.sideQuestCredits)} />
      <Stat label="Boost credits" value={count(climbType.boostCredits)} />
    </Stack>
    <Stack direction="row" gap={3} flexWrap="wrap">
      <Stat
        label="Tick multiplier"
        value={`${decimal(climbType.venueTickMultiplier, 2)}×`}
        help="How much more (or less) this gym's climbers log than the average gym's."
      />
      <Stat
        label="Boost factor"
        value={`${decimal(climbType.boostFactor, 2)}×`}
        help="What the boosts multiply credits by on average, weighted by when people climb."
      />
      <Stat
        label="Score per participant"
        value={count(climbType.score)}
        help={`Model's mean leaderboard score. Active loggers averaged ${count(climbType.observedScore)} in recent months.`}
      />
    </Stack>

    <DetailsTable
      title="Milestones"
      description="Participants reaching each points goal, and the credits they earn before boosts. Reached historically is the share of active loggers whose score got there in recent months."
      headers={["Points goal", "Credits", "Participants", "Credits paid", "Reached historically"]}
      rows={climbType.thresholds.map((threshold) => [
        count(threshold.points),
        count(threshold.creditReward),
        count(threshold.participants),
        count(threshold.credits),
        percent(threshold.observedShare),
      ])}
      empty="No milestones."
    />

    <DetailsTable
      title="Side quests"
      description="Each side quest pays once per climb, up to its limit. Rate is the share of sends that get the action; sends and awards are per participant."
      headers={[
        "Action",
        "Credits",
        "Limit",
        "Rate",
        "Sends",
        "Awards",
        "Hit limit",
        "Credits paid",
      ]}
      rows={climbType.sideQuests.map((sideQuest) => [
        BONUS_TRIGGERS.find((trigger) => trigger.value === sideQuest.trigger)?.name ??
          sideQuest.trigger,
        count(sideQuest.creditReward),
        sideQuest.maxAwardsPerUser === null ? "Unlimited" : count(sideQuest.maxAwardsPerUser),
        percent(sideQuest.rate),
        decimal(sideQuest.sends),
        decimal(sideQuest.awards),
        count(sideQuest.participantsCapped),
        count(sideQuest.credits),
      ])}
      empty="No side quests."
    />

    <DetailsTable
      title="Ability categories"
      description="Tickable routes are the routes up at or below each category's grade. Sends and score are per participant."
      headers={[
        "Category",
        "Mix",
        "Participants",
        "Tickable routes",
        "Tickable points",
        "Score ratio",
        "Sends",
        "Score",
      ]}
      rows={climbType.categories.map((category) => [
        getAbilityCategoryLabel(category.category as AbilityCategory) || category.category,
        percent(category.abilityMix),
        count(category.participants),
        count(category.tickableRoutes),
        count(category.tickablePoints),
        decimal(category.scoreRatio, 2),
        decimal(category.sends),
        count(category.score),
      ])}
      empty="No categories."
    />
  </Stack>
);

const Stat = ({ label, value, help }: { label: string; value: ReactNode; help?: string }) => (
  <Stack sx={{ minWidth: 120, maxWidth: 240 }}>
    <Typography level="body-xs" color="neutral">
      {label}
    </Typography>
    <Typography level="title-md">{value}</Typography>
    {help ? (
      <Typography level="body-xs" color="neutral">
        {help}
      </Typography>
    ) : null}
  </Stack>
);

const DetailsTable = ({
  title,
  description,
  headers,
  rows,
  empty,
}: {
  title: string;
  description: string;
  headers: string[];
  rows: ReactNode[][];
  empty: string;
}) => (
  <Stack gap={0.5}>
    <Typography level="title-sm">{title}</Typography>
    <Typography level="body-xs" color="neutral">
      {description}
    </Typography>
    {rows.length === 0 ? (
      <Typography level="body-sm">{empty}</Typography>
    ) : (
      // Scrolls sideways on its own so a wide table doesn't stretch the dialog.
      <Sheet variant="outlined" sx={{ borderRadius: 8, overflowX: "auto" }}>
        <Table size="sm" sx={{ "& th, & td": { whiteSpace: "nowrap" } }}>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={header} style={{ textAlign: index === 0 ? "left" : "right" }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, index) => (
                  <td key={index} style={{ textAlign: index === 0 ? "left" : "right" }}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>
    )}
  </Stack>
);
